import { isCookieEnabled, getCookie, setCookie, removeCookie } from 'tiny-cookie'
import jwt_decode from 'jwt-decode'
import Api from './api.js'
import app from '~/main'
import Vue from 'vue'
import CryptoJS from 'crypto-js'
import { GetAccountName } from '~/helpers/account'
import IdoniaLocales from '~/locales'
import bus from '~/bus.js'
import router from '~/router'
import useLanguageTree from '~/commons/language'
import useTokenScopesUtil, {
  EMPTY_SCOPE_COLLECTION,
  IDONIA_SCOPE_COLLECTION
} from '~/commons/tokenScopes'
import { v4 as uuidv4 } from 'uuid'
import {useBusUtils} from '~/commons/busUtils'

const { setHandlerAsPromise } = useBusUtils()
const FULL_IDONIA_PERMISSIONS = [
  'create_content',
  'view_content',
  'edit_content',
  'delete_content',
  'share_content']
const {t} = useLanguageTree()
const { getScopes, has, setScopeCollection } = useTokenScopesUtil()
const SUPPORTED_LOCALES = IdoniaLocales.codes
const emptyScopes = { permissions: [], entryPoint: -1, scopeFile: null }
const state = {
  csrfToken: '',
  idToken: '',
  idTokenHint: '',
  token: '',
  accountId: 0,
  accountUUID: '',
  intendedDestination: '',
  locale: IdoniaLocales.defaultLocale.code,
  useIdoniaViewerPro: true,
  email: '',
  password: '',
  account: {
    permissions: [],
    tours: [],
    level: {}
  },
  isAdmin: false,
  lastAvatarUpdate: Date.now(),
  preferredLayout: 'icons',
  preferredOrders: {},
  preferredDICOMUpload: -1,
  expired: false,
  usedStorage: 0,
  platformUserLimit: 100,
  guestSessions: {},
  alerts: [],
  cookiePermission: null,
  lastCookiesPermissionVersion: 2,
  scopes: emptyScopes,
  idoniaScopes: EMPTY_SCOPE_COLLECTION,
  skipGuestTour: false,
  organization: {},
  copyToken: null,
  copyAlert: null,
  remotes: [],
  reachableRemotes: [],
  networkDisconnected: false,
  client: {},
  accountStorageEnabled: false
}

const getters = {
  accountName: state => {
    return GetAccountName(state.account)
  },
  isAuthenticated: state => {
    return state.accountId !== 0 && state.token !== ''
  },
  hasPermission: state => permission => {
    if (typeof state.account.permissions !== 'undefined' && state.account.permissions.length > 0) {
      return state.account.permissions.indexOf(permission) > -1
    }

    return false
  },
  hasGuestAuthentication: state => {
    return Object.keys(state.guestSessions).length > 0
  },
  guestAuthentication: (state) => {
    let authentications = {}
    for (let sha in state.guestSessions) {
      authentications[sha] = 'IDONIA token="' + state.guestSessions[sha] + '",account_id="0"'
    }
    return authentications
  },
  hasRole: state => (...roles) => {
    if (typeof state.account.roles !== 'undefined' && !!state.account.roles.length) {
      let hasIt = false
      roles.forEach(role => {
        hasIt = hasIt || state.account.roles.indexOf(role) > -1
      })
      return hasIt
    }

    return false
  },
  isMine: state => whatever => {
    let ownerID = -1
    const tryOn = ['account_id', 'account_owner.account_id']

    tryOn
      .map(attributes => attributes.split('.'))
      .forEach(attributes => {
        if (ownerID === -1) {
          let researchedValue = whatever
          let valid = true

          attributes.forEach(attribute => {
            if (typeof researchedValue[attribute] !== 'undefined') {
              researchedValue = researchedValue[attribute]
            } else {
              valid = false
            }
          })

          if (valid && !isNaN(researchedValue)) {
            ownerID = researchedValue
          }
        }
      })

    return parseInt(state.account.account_id) === parseInt(ownerID)
  },
  getFilePermission: state => (file) => {
    if (!file || (typeof file.account_owner === 'undefined' && !file.permission)) {
      return '?'
    }
    if (router.app.$route.name.startsWith('public-file')) {
      return 'public'
    }
    if (file.account_owner && file.account_owner.account_id === state.account.account_id) {
      return 'owner'
    }
    return file.permission
  },
  hasFilePermission: (state, getters) => (file, permission) => {
    switch (permission) {
      case 'public':
        return getters['getFilePermission'](file) === 'public'
      case 'read':
        return getters['getFilePermission'](file) === 'read'
      case 'edit':
        return getters['getFilePermission'](file) === 'edit'
      case 'all':
        return getters['getFilePermission'](file) === 'all'
      case 'owner':
        return getters['getFilePermission'](file) === 'owner'
    }

    return false
  },
  hasFilePermissionHigher: (state, getters) => (file, permission) => {
    switch (permission) {
      case 'public':
        if (getters['getFilePermission'](file) === 'public') {
          return true
        }
      // fallthrough
      case 'read':
        if (getters['getFilePermission'](file) === 'read') {
          return true
        }
      // fallthrough
      case 'edit':
        if (getters['getFilePermission'](file) === 'edit') {
          return true
        }
      // fallthrough
      case 'all':
        if (getters['getFilePermission'](file) === 'all') {
          return true
        }
      // fallthrough
      case 'owner':
        if (getters['getFilePermission'](file) === 'owner') {
          return true
        }
      // fallthrough
    }

    return false
  },
  hasFilePermissionLower: (state, getters) => (file, permission) => {
    switch (permission) {
      case 'owner':
        if (getters['getFilePermission'](file) === 'owner') {
          return true
        }
      // fallthrough
      case 'all':
        if (getters['getFilePermission'](file) === 'all') {
          return true
        }
      // fallthrough
      case 'edit':
        if (getters['getFilePermission'](file) === 'edit') {
          return true
        }
      // fallthrough
      case 'read':
        if (getters['getFilePermission'](file) === 'read') {
          return true
        }
      // fallthrough
      case 'public':
        if (getters['getFilePermission'](file) === 'public') {
          return true
        }
      // fallthrough
    }

    return false
  },
  userTours: state => {
    return state.account.tours || []
  },
  accountUUID: state => {
    return state.accountUUID
  },
  scope: (state) => state.scopes ? state.scopes.entryPoint : 0,
  scopeFile: (state) => state.scopes ? state.scopes.scopeFile : null,
  premium: (state) => (state.account.level && state.account.level.subscription ? state.account.level.subscription : {}),
  storagePlus: (state) => (state.account.level && state.account.level.storage ? state.account.level.storage.subscription : []),
  useIdoniaViewerPro: (state, getters, rootState, rootGetters) => {
    if (rootGetters['config/restrictProViewer']) {
      return false
    }

    return state.useIdoniaViewerPro
  },
  getACookie: () => (id) => getCookie('IDONIA_' + id),
  preferredOrders: (state) => state.preferredOrders || {},
  hasIdoniaScope: () => (scope) => has(scope),
  isScoped: (state) => state.idoniaScopes.isScoped,
  hasOrganization: (state) => {
    return state.organization && state.organization.id
  },
  organizationDomain: (state) => {
    if (state.organization && state.organization.domains && state.organization.domains.length > 0) {
      return state.organization.domains[0]
    }
    return ''
  },
  getSelectedRemote: (state) => {
    if (state.remotes && state.remotes.length > 0) {
      return state.remotes.find(remote => remote.selected) || state.remotes[0]
    }
    return null
  },
  getRemote: (state) => (id, scope) => {
    if (state.remotes && state.remotes.length > 0) {
      return state.remotes.find(remote => remote.remote_id === id && remote.scope === scope)
    }
    return null
  },
  usableRemotes: (state) => {
    return (state.reachableRemotes || []).filter(remote =>
      !remote.address ||
      (state.remotes || []).some(accRemote => accRemote.scope === remote.address.scope)
    ) || []
  },
  checkRemoteAuth: (state, getters) => (id, scope, endpoint) => {
    const remote = getters['getRemote'](id, scope)
    if (!remote) {
      return false
    }
    if (!remote.auth || !remote.auth.token || !remote.auth.url) {
      return false
    }
    const url = remote.auth.url.split('?')[0]
    const last = url.split('/').pop()
    if (last !== endpoint) {
      return false
    }
    const decoded = jwt_decode(remote.auth.token)
    const now = Date.now() / 1000
    if (decoded.exp < now) {
      return false
    }
    return remote.auth
  },
  getClient: (state) => state.client,
  accountHasUiLogoutConfigured: (state) => {
    return state.account && state.account.config && state.account.config.ui_only_logout
  }
}

const mutations = {
  setPermissions (state, permissions) {
    state.account.permissions.splice(0, state.account.permissions.length)
    state.account.permissions.push(...permissions)
  },
  setCSRFToken (state, csrfToken) {
    state.csrfToken = csrfToken
  },
  setIdToken (state, idToken) {
    state.idToken = idToken
  },
  idoniaViewerPro (state, flag) {
    state.useIdoniaViewerPro = flag
  },
  updateLastAvatarUpdate (state, timestamp) {
    state.lastAvatarUpdate = timestamp
  },
  updateStorage (state, {usedStorage, platformUserLimit}) {
    state.usedStorage = usedStorage
    state.platformUserLimit = platformUserLimit
  },
  authentication (state, authentication) {
    state.token = authentication.token || ''
    state.accountId = authentication.accountId || 0
    state.scopeHash = authentication.scopeHash || ''
    state.isAdmin = false
  },
  credentials (state, credentials) {
    state.email = credentials.email || ''
    state.password = credentials.password || ''
  },
  locale (state, locale) {
    state.locale = locale
    bus.$emit('viewer|updateLocale', locale)
  },
  pushAlert (state, alert) {
    if (!state.alerts) state.alerts = []
    state.alerts.push(alert)
  },
  setAlerts (state, alerts) {
    state.alerts = alerts
  },
  removeAlert (state, alert) {
    const index = state.alerts.findIndex((a) => a._alertID === alert._alertID)
    if (index > 0) state.alerts.splice(index, 1)
  },
  clearAlerts (state) {
    state.alerts = []
    bus.$emit('session|clearAlerts')
  },
  preferredLayout (state, preferredLayout) {
    state.preferredLayout = preferredLayout
  },
  setPreferredOrder (state, {key, value}) {
    if (!state.preferredOrders) {
      state.preferredOrders = {}
    }
    if (!state.preferredOrders.files) {
      state.preferredOrders.files = { sort: 'Date', order: 'down' }
    }
    if (!state.preferredOrders.files.sort) {
      state.preferredOrders.files.sort = 'Date'
    }
    state.preferredOrders[key] = {...state.preferredOrders[key], ...value}
  },
  setPreferredDICOMUpload (state, preferredDICOMUpload) {
    state.preferredDICOMUpload = preferredDICOMUpload
  },
  intendedDestination (state, intendedDestination) {
    state.intendedDestination = intendedDestination
  },
  loadFromCookie (state) {
    if (isCookieEnabled()) {
      let locale = SUPPORTED_LOCALES.indexOf(getCookie('IDONIA_locale')) > -1 ? getCookie('IDONIA_locale') : ''
      state.locale = locale || state.locale
      state.roles = getCookie('IDONIA_roles') || ''
      state.preferredLayout = getCookie('IDONIA_preferred_layout') || 'icons'
      try {
        state.preferredOrders = JSON.parse(getCookie('IDONIA_preferred_order'))
      } catch (e) {
        state.preferredOrders = {}
      }
      try {
        state.preferredDICOMUpload = JSON.parse(getCookie('IDONIA_preferred_dicom_upload'))
      } catch (e) {
        state.preferredDICOMUpload = -1
      }
      state.expired = JSON.parse(getCookie('IDONIA_expired')) || false
      state.cookiePermission = getCookie('IDONIA_cookie_permission') || null
      state.intendedDestination = getCookie('IDONIA_intendedDestination') || ''
      state.useIdoniaViewerPro = JSON.parse(getCookie('IDONIA_use_idonia_viewer_pro_v2') || 'true')
    } else {
      state.idToken = Api.config.idToken
    }
  },
  clearCookie () {
    removeCookie('IDONIA_roles', {})
    removeCookie('IDONIA_expired', {})
    removeCookie('IDONIA_preferred_dicom_upload', {})
  },
  saveACookie (state, {id, value}) {
    if (parseInt(state.cookiePermission) === state.lastCookiesPermissionVersion) {
      if (id === 'account_id' || id === 'token' || id === 'id_token') {
        return
      }
      setCookie('IDONIA_' + id, value, {})
    }
  },
  clearACookie (state, id) {
    removeCookie('IDONIA_' + id, {})
    if (id === 'token' || id === 'account_id') {
      const parentDomains = walkDomain(window.location.hostname)
      for (let i = 0; i < parentDomains.length; i++) {
        removeCookie('IDONIA_' + id, { domain: parentDomains[i] })
      }
    }
  },
  saveToCookie (state) {
    setCookie('IDONIA_locale', state.locale, {})
    setCookie('IDONIA_use_idonia_viewer_pro_v2', state.useIdoniaViewerPro, true)
    if (parseInt(state.cookiePermission) === state.lastCookiesPermissionVersion) {
      setCookie('IDONIA_roles', state.roles, {})
      setCookie('IDONIA_intendedDestination', state.intendedDestination)
      setCookie('IDONIA_preferred_layout', state.preferredLayout, {})
      setCookie('IDONIA_preferred_order', JSON.stringify(state.preferredOrders), {})
      setCookie('IDONIA_preferred_dicom_upload', state.preferredDICOMUpload, {})
      setCookie('IDONIA_expired', state.expired, {})
    }
    if (state.cookiePermission !== null) {
      setCookie('IDONIA_cookie_permission', state.cookiePermission, {})
    }
  },
  setRoles (state, roles) {
    state.roles = roles
    state.isAdmin = roles.indexOf('administrator') >= 0
  },
  setAccount (state, account) {
    state.account = account
  },
  setExpire (state, date) {
    if (date !== null) {
      let now = new Date()
      date = new Date(date)
      state.expired = false
      if (date <= now) {
        state.expired = true
      }
    } else {
      state.expired = false
    }
  },
  clearAllGuestSessions (state) {
    state.guestSessions = {}
  },
  addGuestSession (state, {sid, token}) {
    Api.config.token = token
    state.guestSessions = {
      ...state.guestSessions,
      [CryptoJS.enc.Hex.stringify(CryptoJS.SHA256(sid))]: token
    }
  },
  setCookiePermission (state, permission) {
    state.cookiePermission = permission
  },
  addTour (state, tour) {
    if (!state.account.tours) {
      state.account.tours = []
    }
    state.account.tours.push(tour)
  },
  discardTour (state, tour) {
    let index = state.account.tours.indexOf(tour)
    if (index > -1) {
      state.account.tours.splice(index, 1)
    }
  },
  setLevel (state, level) {
    state.account.level = level
    state.account = Object.assign({}, state.account)
  },
  setAccountUUID (state, uuid) {
    state.accountUUID = uuid
  },
  setScopes (state, scopes) {
    state.scopes = scopes || emptyScopes
  },
  setIdoniaScopes (state, scopes) {
    state.idoniaScopes = scopes || EMPTY_SCOPE_COLLECTION
  },
  setSkipGuestTour (state, skip) {
    state.skipGuestTour = !!skip
  },
  setOrganization (state, organization) {
    state.organization = organization
  },
  setCopyToken (state, token) {
    state.copyToken = token
  },
  clearCopyToken (state) {
    state.copyToken = null
  },
  setCopyAlert (state, alert) {
    state.copyAlert = alert
  },
  clearCopyAlert (state) {
    state.copyAlert = null
  },
  setRemotes (state, remotes) {
    state.remotes = remotes
  },
  fillRemote (state, {id, scope, newData}) {
    let index = state.remotes.findIndex(r => r.remote_id === id && r.scope === scope)
    if (index > -1) {
      state.remotes[index] = Object.assign({}, state.remotes[index], newData)
    }
  },
  clearRemotes (state) {
    state.remotes = []
  },
  unselectAllRemotes (state) {
    state.remotes.forEach(r => {
      r.selected = false
    })
  },
  setReachableRemotes (state, remotes) {
    state.reachableRemotes = remotes
  },
  setNetworkStatus (state, disconnected) {
    state.networkDisconnected = disconnected
  },
  setClient (state, client) {
    state.client = client
  }
}

const actions = {
  getCSRFToken ({commit}) {
    return Api.getV2('csrf-token').then(response => {
      commit('setCSRFToken', response.body)
      return response.body
    })
  },
  avatarUpdated ({commit}) {
    commit('updateLastAvatarUpdate', Date.now())
  },
  idoniaViewerPro ({commit}, flag) {
    commit('idoniaViewerPro', flag)
    commit('saveToCookie')
  },
  storageUpdated ({commit}) {
    if (!state.accountStorageEnabled) {
      return null
    }
    if (state.token) {
      Api.config.token = state.token
      Api.config.accountId = state.accountId
      return Api.getV2('account-storage').then(response => {
        commit('updateStorage', response.body)
        return response
      })
    } else {
      return null
    }
  },
  authentication ({commit, dispatch}, {token, accountId}) {
    commit('authentication', {token, accountId})
    return dispatch('initializeSession', {token, accountId})
  },
  locale ({commit}, locale) {
    if (SUPPORTED_LOCALES.indexOf(locale) === -1) {
      locale = IdoniaLocales.defaultLocale.code
    }
    commit('locale', locale)
    commit('saveToCookie')
    bus.$emit('updateLocale', locale)
    if (app) {
      app.$moment.locale(locale.slice(0, 2))
      app.$i18n.locale = locale
    }
    let params = new URLSearchParams(window.location.search.slice(1))
    params.delete('hl')
    window.history.replaceState(null, null, window.location.pathname + '?' + params.toString() + window.location.hash)
  },
  preferredLayout ({commit}, preferredLayout) {
    commit('preferredLayout', preferredLayout)
    commit('saveToCookie')
  },
  setPreferredOrder ({commit}, preferredOrder) {
    commit('setPreferredOrder', preferredOrder)
    commit('saveToCookie')
  },
  setPreferredDICOMUpload ({commit}, preferredDICOMUpload) {
    commit('setPreferredDICOMUpload', preferredDICOMUpload)
    commit('saveToCookie')
  },
  intendedDestination ({commit}, intendedDestination) {
    commit('intendedDestination', intendedDestination)
    commit('saveToCookie')
    return new Promise(resolve => {
      resolve()
    })
  },
  saveACookie ({commit}, {id, value}) {
    commit('saveACookie', {id, value})
  },
  loadFromCookie ({commit, state, dispatch}, withConsent) {
    commit('loadFromCookie')
    if (withConsent) {
      commit('setCookiePermission', state.lastCookiesPermissionVersion)
    }
    if (state.idToken !== '') {
      Api.config.idToken = state.idToken
    }
    Api.config.token = state.token
    Api.config.accountId = state.accountId
    return dispatch('loadAccountData')
  },
  checkSession ({commit, state, dispatch}, idToken) {
    if (idToken) {
      const decoded = jwt_decode(idToken)
      if (decoded) {
        Api.config.idToken = idToken
      }
    }
    const expiration = parseInt(Vue.prototype.$config.expireSessionTime, 10)
    return Api.getV2('account/session', { params: {expiration} }).then((response) => {
      commit('setCookiePermission', state.lastCookiesPermissionVersion)
      commit('setIdToken', idToken)
      Api.config.accountId = response.body.account_id
      Api.config.scopeHash = response.body.scope_hash
      commit('authentication', {
        token: response.body.token,
        accountId: response.body.account_id,
        scopeHash: response.body.scope_hash
      })
    }).catch(response => {
      console.log('checkSession catch', response)
      throw response
    })
  },
  getScope ({commit, state, dispatch}) {
    return Api.getV2('account/scopes').then((response) => {
      Api.getV2('file/' + response.body.entry_point.toString()).then((file) => {
        commit('setScopes', {
          permissions: response.body.permissions,
          entryPoint: response.body.entry_point,
          scopeFile: file.body
        })
        commit('setPermissions', response.body.permissions)
        // TODO change this if someday is allowed to have more than one file scoped
      }).catch(e => {
        console.log('getScopes catch', e)
        // dispatch('destroySession')
      })
    }).catch(response => {
      console.log('getScopes catch', response)
      dispatch('destroySession')
    })
  },
  timeoutCookieExpiration ({state, dispatch, getters}, expiration) {
    if (getters.isAuthenticated) {
      Api.postV2('account/expiration-token', {
        data: { expiration_time_seconds: expiration },
        skipAuth: true
      }).catch(err => {
        console.error('expiration time error: ', err)
      })
    }
  },
  saveSession ({commit, state}, credentials) {
    commit('setCookiePermission', state.lastCookiesPermissionVersion)
    commit('authentication', {token: credentials.token, accountId: credentials.accountID})
    Api.config.token = credentials.token
    Api.config.accountId = credentials.accountID
    commit('saveToCookie')
  },
  destroySession ({state, dispatch, getters}) {
    console.log('destroy', state)
    const doLogout = () => {
      Api.config.destroyingSession = true
      return Api.postV2('account/logout', {
        data: {
          token: state.idTokenHint,
          ui_only: getters.accountHasUiLogoutConfigured
        }
      })
    }
    return doLogout()
      .catch(() => {
        Api.config.destroyingSession = false
        return dispatch('getCSRFToken').then(doLogout)
      })
      .then(response => {
        console.log('logged out: ', response)
      }).catch(err => {
        console.error('log out failed: ', err)
      }).finally(async () => {
        await dispatch('logout')
      })
  },
  destroyGuestSession ({commit, state, getters}) {
    const tokens = Object.values(state.guestSessions)
    if (tokens.length === 0) {
      return Promise.resolve()
    }
    let authHeader
    if (!getters.isAuthenticated) {
      const guestAuths = Object.values(getters.guestAuthentication)
      if (guestAuths.length > 0) {
        authHeader = guestAuths[0]
      }
    }
    return Api.postV2('guest/logout', {
      data: {
        tokens
      },
      headers: {
        Authorization: authHeader
      }
    }).then(response => {
      console.log('guest logged out: ', response)
    }).catch(err => {
      console.error('guest log out failed: ', err)
    }).finally(() => {
      commit('clearAllGuestSessions')
      Api.config.token = ''
    })
  },
  logout ({commit, dispatch}) {
    Api.config.destroyingSession = true
    commit('authentication', {token: '', accountId: 0})
    commit('setIdToken', '')
    commit('clearAlerts')
    commit('setIdoniaScopes', null)
    commit('setAccount', {permissions: []})
    commit('clearCookie')
    commit('clearRemotes')
    dispatch('notifications/clearWebsocket', null, {root: true})
    dispatch('dialog/closeDialog', null, {root: true})
    Api.config.destroyingSession = false
  },
  async login ({commit, state, dispatch, getters}, credentials) {
    let email = hash(credentials.email.toLowerCase())
    let password = hash(credentials.password)

    commit('credentials', {email, password})

    const expiration = parseInt(Vue.prototype.$config.expireSessionTime, 10)

    const data = {email, password, code_2fa: credentials.code_2fa, expiration_time_seconds: expiration}
    if (credentials.challenge) {
      data.challenge = credentials.challenge
    }
    await dispatch('getCSRFToken')
    const response = await Api.postV2('account/login', {
      data: data,
      skipAuth: true
    })

    localStorage.removeItem('mobileAccessShown')
    await dispatch('getCSRFToken')
    return dispatch('initializeSession', {
      token: response.body.token,
      accountId: response.body.account_id
    })
  },
  signInOrUp ({commit, state, dispatch}, payload) {
    const email = payload.email.toLowerCase()
    const password = hash(payload.password)

    commit('credentials', {email: hash(email), password})

    const reqData = {
      data: {
        email,
        password,
        code_2fa: payload.code_2fa,
        language: payload.language,
        terms: payload.terms,
        privacy: payload.privacy,
        challenge: payload.challenge
      },
      skipAuth: true
    }

    return dispatch('getCSRFToken').then(() => {
      return Api.postV2('account/sign-in-or-up', reqData)
    }).then(response => {
      dispatch('initializeSession', {
        token: response.body.token,
        accountId: response.body.account_id
      })
      return response.body.created
    })
  },
  initializeSession ({commit, state, dispatch}, {token, accountId}) {
    commit('authentication', {token, accountId})
    Api.config.token = token
    Api.config.accountId = accountId
    commit('saveToCookie')
    commit('credentials', {email: '', password: ''})
    return dispatch('loadAccountData')
  },
  loadLevel ({commit}) {
    return Api.getV2('account/level').then(response => {
      commit('setLevel', response.body)
    })
  },
  saveAccountData ({commit, dispatch}, account) {
    commit('setAccount', account)
    dispatch('contracts/loadAccepted', null, {root: true})
  },
  async loadAccountData ({commit, state, dispatch, getters}) {
    let scopes = EMPTY_SCOPE_COLLECTION
    if (!state.idToken || state.idToken === '') {
      scopes = setScopeCollection(IDONIA_SCOPE_COLLECTION)
    } else {
      scopes = getScopes(state.idToken)
    }
    commit('setIdoniaScopes', scopes)
    if (!state.idoniaScopes.isScoped) {
      commit('setPermissions', FULL_IDONIA_PERMISSIONS)
    } else {
      await dispatch('getScope')
    }
    return Api.getV2('account').then(async response => {
      await dispatch('getCSRFToken')
      commit('setAccount', response.body.account)
      commit('setRoles', response.body.account.roles)
      commit('setExpire', response.body.account.expire_date)
      commit('setAccountUUID', response.body.account.account_uuid)
      commit('setRemotes', response.body.account.remotes)
      bus.$emit('loadedAccountUUID', response.body.account.account_uuid)
      if (response.body.account.organization_id) {
        await Api.getV2('organization/' + response.body.account.organization_id).then(response => {
          commit('setOrganization', response.body)
        })
      }
      if (response.body.account.preferred_lang) {
        dispatch('locale', response.body.account.preferred_lang)
      }
      dispatch('getReachableRemotes')
      if (response.body.scopes) {
        scopes = setScopeCollection(
          {
            scopeMask: BigInt(response.body.scopes.scope_mask),
            arguments: response.body.scopes.arguments
          }
        )
        commit('setIdoniaScopes', scopes)
      }
      commit('saveToCookie')
      dispatch('contracts/loadAccepted', null, {root: true})
      // used for subscriptions
      // dispatch('loadLevel')
      if (state.copyToken) dispatch('useCopyToken')
      if (state.copyAlert) dispatch('showCopyAlert')
      return response.body.account
    })
  },
  guestLogin ({commit, state, dispatch}, payload) {
    return Api.postV2('guest/login', {
      data: payload,
      skipAuth: true,
      skipCredentials: true
    }).then(async response => {
      // Check tour cookie & show tour if not found
      if (!getCookie('IDONIA_guest_tour_watched') && !state.skipGuestTour) {
        commit('addTour', 'guestTour')
      }
      commit('setAlerts', response.body.alerts)
      commit('addGuestSession', {token: response.body.token, sid: payload.sid})
      return response
    }).catch(e => {
      console.error(e)
      e.statusText = e.Error || t('Try again later')
      throw e
    })
  },
  roles ({commit}) {
    return Api.getV2('account').then(response => {
      commit('setAccount', response.body.account)
      commit('setRoles', response.body.account.roles)
      return response
    })
  },
  sendForgotPassword ({commit, state}, params) {
    return Api.postV2('forgot-password', {
      data: params,
      skipAuth: true
    })
  },
  checkToken ({commit, state}, params) {
    return Api.getV2('reset-password/' + params)
  },
  resetPassword ({commit, state}, {token, params}) {
    return Api.postV2('reset-password/' + token, {
      data: params,
      skipAuth: true
    })
  },
  checkRegister ({commit, state}, params) {
    return Api.getV2('register/' + params)
  },
  register ({commit, state}, data) {
    data.password = hash(data.password)
    return Api.postV2('register', {data})
  },
  setCookiePermission ({commit}, payload) {
    commit('setCookiePermission', payload)
    commit('saveToCookie')
  },
  discardTour ({commit}, payload) {
    if (payload === 'guestTour') {
      commit('saveACookie', {id: 'guest_tour_watched', value: true})
      commit('discardTour', payload)
      return
    }
    return Api.deleteV2('account/tour/' + payload, {}).then(response => {
      commit('discardTour', payload)
      console.log(response)
    }).catch(error => {
      console.log(error)
    })
  },
  clearAlerts ({commit}) {
    commit('clearAlerts')
  },
  pushAlert ({commit}, payload = {}) {
    const id = uuidv4()
    payload._alertID = id
    commit('pushAlert', payload)
    return id
  },
  pushAlertAfterClear ({commit, dispatch}, payload = {}) {
    return setHandlerAsPromise(bus, 'session|clearAlerts', () => {
      return dispatch('pushAlert', payload)
    })
  },
  removeAlert ({commit}, payload) {
    commit('removeAlert', payload)
  },
  addTour ({commit}, payload) {
    return Api.postV2('account/tour/' + payload, {}).then(response => {
      commit('addTour', payload)
      console.log(response)
    }).catch(error => {
      console.log(error)
    })
  },
  skipGuestTour ({commit}, payload) {
    commit('setSkipGuestTour', payload)
  },
  setCopyToken ({commit}, payload) {
    commit('setCopyToken', payload)
  },
  useCopyToken ({commit, state, dispatch}) {
    return Api.postV2(`file/${state.copyToken}/copy`)
      .then((response) => {
        if (!response.ok) {
          throw response
        }
        dispatch('feedback/addSnack', {text: 'copy.verificationSuccess', type: 'success', timeout: 10000}, {root: true})
      })
      .catch((error) => {
        console.log(error)
        dispatch('feedback/addCatchSnack', error, {root: true})
      })
      .finally(() => {
        commit('clearCopyToken')
      })
  },
  setCopyAlert ({commit}, payload) {
    if (!payload) {
      commit('clearCopyAlert')
      return
    }
    commit('setCopyAlert', 'copy.started')
  },
  showCopyAlert ({commit, dispatch}) {
    dispatch('pushAlert', {
      type: 'info',
      message: state.copyAlert
    })
    commit('clearCopyAlert')
  },
  getRemoteAuth ({commit, getters}, {remote, scope, endpoint, params}) {
    const auth = getters['checkRemoteAuth'](remote, scope, endpoint)
    if (auth) {
      return Promise.resolve(auth)
    }
    return Api.getV2(`remote/auth/${remote}/${scope}/${endpoint}`, {
      params
    }).then(response => {
      commit('fillRemote', { id: remote, scope, newData: { auth: response.body } })
      return response.body
    })
  },
  selectRemote ({commit, getters}, {remote, scope}) {
    commit('unselectAllRemotes')
    commit('fillRemote', { id: remote, scope, newData: { selected: true } })
  },
  setNetworkDiconnected ({commit}, disconnected) {
    commit('setNetworkStatus', disconnected)
  },
  checkClient ({commit}) {
    return Api.postV2('client/check').then(response => {
      if (response.status === 200) {
        commit('setClient', response.body || {})
      }
    }).catch(async response => {
      if (response.status === 417) {
        response = await Api.postV2('client/check', {
          data: {
            origin: window.location.origin || window.origin,
            ...response.body
          }
        })
        if (response.status === 200) {
          commit('setClient', response.body || {})
        }
      }
    })
  },
  getReachableRemotes ({commit}) {
    return Api.getV2('account/remote-scopes').then(response => {
      const remotes = response.body || []
      if (remotes.length > 0) {
        remotes.sort(sortByName)
        remotes.unshift({ address: null, name: t('noScopeFilter') })
      }
      commit('setReachableRemotes', remotes)
    })
  }
}

function sortByName (a, b) {
  const nameA = a.name.toUpperCase()
  const nameB = b.name.toUpperCase()
  return nameA.localeCompare(nameB)
}

export function hash (val) {
  return CryptoJS.enc.Base64.stringify(CryptoJS.SHA256(val))
}

function walkDomain (hostname) {
  let parts = hostname.split('.')
  const parents = []
  for (let i = 0; i < parts.length - 1; i++) {
    parents.push(parts.slice(i).join('.'))
    parents.push('.' + parts.slice(i).join('.'))
  }
  return parents
}
export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
  hash
}



// WEBPACK FOOTER //
// ./src/store/idonia-api/session.js